// Detail-popover hydration (SH-1986, decision E1). The SH-1408/1419 feed carries
// only id/title/times/type/entity ref — NOT the primary contact, owner, or total
// value the detail modals show. Those are fetched on modal open from the linked
// record, keyed on the event's entityType + entityId. Any failure — a deleted
// record (404) or a transient 500/network error — degrades to a graceful message
// rather than crashing the popover (AC: deleted lead/opp). The fetch surfaces no
// status code, so the two are not distinguished; the copy is worded to cover
// both rather than claiming the record was deleted.
//
// The fetch deliberately does not go through the lead/opportunity detail thunks
// — see api/fetchEventRecord.ts for why.

import { useEffect, useState } from 'react'
import { fetchEventRecord } from '../api/fetchEventRecord'
import type { CalendarEvent } from '../typings'

export type EventDetailData = {
  primaryContactName?: string
  owner?: string
  totalValue?: number
}

export type UseEventDetailResult = {
  isLoading: boolean
  /** The linked record could not be loaded — deleted (404) or a transient failure. */
  loadError: boolean
  data: EventDetailData | null
}

const IDLE: UseEventDetailResult = {
  isLoading: false,
  loadError: false,
  data: null,
}

// Per-session cache of hydrated records, keyed by `entityType:entityId` (E1:
// "cache per event.id"). Reopening any event linked to an already-loaded record
// is instant — no refetch, no skeleton. SH-1432 can now mutate a linked record
// (parent-date opt-in, or a delete), so invalidate the affected key after a
// successful write — see invalidateEventDetail.
const recordCache = new Map<string, EventDetailData>()

/** Test seam: drop the module-level cache so cases don't leak into each other. */
export function _clearEventDetailCache(): void {
  recordCache.clear()
}

/**
 * SH-1432: drop a single linked record from the hydration cache after an edit or
 * delete mutates it, so the next popover open re-fetches instead of showing a
 * stale primary contact / owner / value. Keyed identically to the cache
 * (`entityType:entityId`); a no-op when the event has no linked record.
 */
export function invalidateEventDetail(
  entityType: CalendarEvent['entityType'],
  entityId: number | undefined,
): void {
  if (!entityType || entityId == null) return
  recordCache.delete(`${entityType}:${entityId}`)
}

function contactName(contact?: {
  firstName?: string | null
  lastName?: string | null
}): string | undefined {
  if (!contact) return undefined
  const name = `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim()
  return name || undefined
}

// Lead and Opportunity details both extend IEntityDetails, so the popover's
// fields read the same off either — the primary contact, the assignee (shown as
// "Owner"), and the record's total value.
function toDetailData(entity: {
  primaryContact?: { firstName?: string | null; lastName?: string | null }
  assignee?: { fullName?: string | null } | null
  totalLeadValue?: number
}): EventDetailData {
  return {
    primaryContactName: contactName(entity.primaryContact),
    owner: entity.assignee?.fullName ?? undefined,
    totalValue: entity.totalLeadValue,
  }
}

/**
 * Hydrates `event`'s linked record when a popover for it is open. Pass `null`
 * (popover closed) to stay idle and issue no request. Opportunities read the
 * Opportunity endpoint; Leads and PendingLeads share the Lead endpoint (E1) —
 * directly, never via the detail thunks (see api/fetchEventRecord.ts).
 */
export function useEventDetail(
  event: CalendarEvent | null,
): UseEventDetailResult {
  // Settled results are tagged with the request they answer. When the popover
  // switches to a different event, that key changes and the hook reports loading
  // on the SAME render — so the previous event's owner/contact/value can never
  // flash under the new card while its fetch is still in flight.
  const [settled, setSettled] = useState<{
    key: string
    loadError: boolean
    data: EventDetailData | null
  } | null>(null)

  const entityType = event?.entityType
  const entityId = event?.entityId
  // Keyed by record, not event: two events on the same lead share one fetch.
  const recordKey =
    event && entityType && entityId != null ? `${entityType}:${entityId}` : null
  const cached = recordKey ? recordCache.get(recordKey) : undefined

  useEffect(() => {
    // Nothing to hydrate, or already cached from an earlier open → no fetch.
    if (
      !recordKey ||
      !entityType ||
      entityId == null ||
      recordCache.has(recordKey)
    )
      return

    // Abort rather than just ignoring the result: `get` redirects the app on a
    // 401, so a request left in flight after the popover has gone could
    // navigate out from under the user. Same reason useCalendarEvents aborts.
    const controller = new AbortController()

    fetchEventRecord(entityType, entityId, controller.signal)
      .then(toDetailData)
      .then((data) => {
        recordCache.set(recordKey, data)
        if (!controller.signal.aborted)
          setSettled({ key: recordKey, loadError: false, data })
      })
      .catch(() => {
        // An abort is not a load failure — the popover is already gone.
        if (!controller.signal.aborted)
          setSettled({ key: recordKey, loadError: true, data: null })
      })

    return () => {
      controller.abort()
    }
  }, [recordKey, entityType, entityId])

  // Nothing to hydrate (no linked record) → idle, no loading row.
  if (!recordKey) return IDLE
  // Cache hit → instant, no skeleton.
  if (cached) return { isLoading: false, loadError: false, data: cached }
  // A settled result for a different record is stale; the current one is loading.
  if (settled?.key !== recordKey) {
    return { isLoading: true, loadError: false, data: null }
  }
  return { isLoading: false, loadError: settled.loadError, data: settled.data }
}
